import { ReactElement } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Grid } from '@material-ui/core';
import { AddCircleOutline } from '@mui/icons-material';

import validate from './form/validate';
import Modal from '~/ui/components/common/Modal';
import Button from '~/ui/components/common/Button';

import { IFormFields } from './form/types';
import styles from './MedicationModal.module.scss';
import CarePlanMedication from './CarePlanMedication';
import { ICarePlanMedication } from '~/services/api/carePlanMedications/types';
import getInitialValues from '../../Medications/form/getInitialValues';

interface IProps {
  mode?: 'create' | 'edit';
  initialValues: ICarePlanMedication;
  isProcessing: boolean;
  onClose: () => void;
  onSubmit: (values: ICarePlanMedication[]) => void;
}

const MedicationModal = ({
  mode = 'create',
  initialValues,
  isProcessing,
  onClose,
  onSubmit,
}: IProps): ReactElement => {
  const title = mode === 'create' ? 'Add Medication' : 'Update Medication';

  const formMethods = useForm({
    resolver: validate,
    defaultValues: {
      carePlanMedications: [initialValues],
    },
  });

  const { control, handleSubmit } = formMethods;

  const { fields, append, remove } = useFieldArray<IFormFields, 'carePlanMedications', ''>({
    control,
    name: 'carePlanMedications',
    keyName: '',
  });

  const onAdd = () => append(getInitialValues({} as ICarePlanMedication));

  const onRemove = (index: number) => remove(index);

  const getKey = (id: number, index: number) => `${id}-${index}`;

  return (
    <Modal open maxWidth="lg" title={title} className={styles.modal} onClose={onClose}>
      <div className={styles.inner}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(values => onSubmit(values.carePlanMedications))}>
            <Grid container spacing={3}>
              {fields.map((carePlanMedication, index) => (
                <Grid item xs={12} key={getKey(carePlanMedication.id, index)}>
                  <CarePlanMedication
                    index={fields.indexOf(carePlanMedication)}
                    isDeletable={fields.length > 1}
                    onRemove={onRemove}
                  />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  {mode === 'create' && (
                    <Grid item>
                      <Button
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<AddCircleOutline />}
                        onClick={onAdd}
                      >
                        Add More
                      </Button>
                    </Grid>
                  )}
                  <Grid item xs />
                  <Grid item>
                    <Button
                      variant="outlined"
                      color="default"
                      disabled={isProcessing}
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      isLoading={isProcessing}
                      disabled={isProcessing}
                    >
                      {mode === 'create' ? 'Create' : 'Save'}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
};

export default MedicationModal;
